import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import BasicFields from "./BasicFields";
import UserFields from "./UserFields";
import { ChatContext } from "../../../../contexts/ChatContexts";
import { GET_EBOOK } from "../../../../apis/constants.api";
import { allowSchema, bookSchema } from "../form.schema";
import { updatePublishEBook } from "../../../../apis/ebook.api";
import { toast } from "react-toastify";

export default function EbookEditModal(props) {
  const { book, isEditOpen, onEditClose } = props;
  const [activeTab, setActiveTab] = useState(0);
  const queryClient = useQueryClient();
  const [bookAccessList, setBookAccessList] = useState(book?.allowList || []);
  const [isAllowedForAll, setIsAllowedForAll] = useState(book?.allowedForAll);
  const [chatState, ___, { channelDialogToogle }] = useContext(ChatContext);
  const { channelTabDialogOpen } = chatState || {};

  if (!channelTabDialogOpen) {
    onEditClose();
    channelDialogToogle();
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    trigger,
    register,
    watch,
    formState,
  } = useForm({
    defaultValues: {
      thumbnailUrl: book.thumbnailUrl,
      title: book.title,
      pdfUrl: book.pdfUrl,
      relevantSkills: book.relevantSkills.map((item) => ({
        value: item._id,
        label: item.name,
      })),
      accessList: [],
      allowedForAll: book.allowedForAll,
    },
    resolver: yupResolver(activeTab === 0 ? bookSchema : allowSchema),
  });
  const onSubmit = async (values) => {
    const payload = {
      ...values,
      accessList: [
        ...bookAccessList.map((member) => member._id),
        ...values.accessList.map((user) => user.value),
      ],
      relevantSkills: values.relevantSkills.map((skill) => skill.value),
    };

    const bookId = book._id;
    updatePublishEBooks({ bookId, ...payload });
  };

  const { mutate: updatePublishEBooks, isLoading: isUpdating } = useMutation(
    updatePublishEBook,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_EBOOK] });
        reset();
        onEditClose();
        toast({
          title: "Book updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      },
    }
  );

  const handleClose = useCallback(() => {
    reset();
    onEditClose?.();
  }, [reset, onEditClose]);

  const handleTabClick = async (index) => {
    if (index > activeTab) {
      const isValid = await trigger();
      if (isValid) {
        setActiveTab(index);
      }
    } else {
      setActiveTab(index);
    }
  };

  useEffect(() => {
    if (book && isEditOpen) {
      reset({
        thumbnailUrl: book.thumbnailUrl,
        title: book.title,
        pdfUrl: book.pdfUrl,
        relevantSkills: book.relevantSkills.map((item) => ({
          value: item._id,
          label: item.name,
        })),
        accessList: [],
        allowedForAll: book?.allowedForAll,
      });
      setBookAccessList(book?.accessList || []);
      setIsAllowedForAll(book.allowedForAll);
    }
  }, [book, reset, isEditOpen]);

  const handleRemove = (userId) => {
    const newValues = bookAccessList.filter((user) => user?._id !== userId);

    setBookAccessList(newValues);
  };

  return (
    <Modal
      onClose={handleClose}
      isOpen={isEditOpen}
      isCentered
      closeOnOverlayClick={false}
    >
      <form>
        <ModalOverlay />
        <ModalContent minW="600px">
          <ModalHeader>{"Update Book"}</ModalHeader>
          <ModalCloseButton border="1px solid #868B944D" />
          <ModalBody>
            <Tabs
              position="relative"
              variant="unstyled"
              index={activeTab}
              onChange={handleTabClick}
            >
              <Flex
                justifyContent={"flex-start"}
                borderBottom="1px solid #E3E4E8"
                pb="10px"
                flexWrap={{ md: "wrap-reverse", "2xl": "nowrap" }}
                gap="20px"
              >
                <TabList width={{ base: "100%", xl: "auto" }}>
                  <Flex width={{ base: "100%", xl: "auto" }}>
                    <Tab
                      flex={{ base: "1", xl: "none" }}
                      color={activeTab === 0 ? "#713EDD" : "#868B94"}
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        gap={{ base: "5px", xl: "12px" }}
                      >
                        <Text
                          fontSize={{ base: "fs.14", lg: "fs.16" }}
                          fontWeight="fw.400"
                        >
                          Basic Info
                        </Text>
                      </Flex>
                    </Tab>
                    <Tab
                      flex={{ base: "1", xl: "none" }}
                      color={activeTab === 1 ? "#713EDD" : "#868B94"}
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        gap={{ base: "5px", xl: "12px" }}
                      >
                        <Text
                          fontSize={{ base: "fs.14", lg: "fs.16" }}
                          fontWeight="fw.400"
                        >
                          Allow users
                        </Text>
                      </Flex>
                    </Tab>
                  </Flex>
                </TabList>
              </Flex>

              <TabIndicator
                mt="-4.5px"
                height="4px"
                bg="#713EDD"
                width="auto"
                borderRadius="1px"
              />

              <TabPanels
                height={{
                  md: "calc(100vh - 270px)",
                  lg: "calc(100dvh - 310px)",
                  "2xl": "calc(100dvh - 250px)",
                }}
                overflowY="scroll"
                className="scrollBar"
              >
                <TabPanel>
                  <BasicFields
                    book={book}
                    control={control}
                    errors={errors}
                    register={register}
                  />
                </TabPanel>
                <TabPanel>
                  <UserFields
                    book={book}
                    control={control}
                    errors={errors}
                    bookAccessList={bookAccessList}
                    handleRemove={handleRemove}
                    watch={watch}
                    isAllowedForAll={isAllowedForAll}
                    setIsAllowedForAll={setIsAllowedForAll}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>

          <ModalFooter background="#F5F4F6" borderRadius="0px 0px 10px 10px">
            <Flex gap="10px">
              <Button
                background="#EAE9EC"
                onClick={() => {
                  onEditClose();
                  handleTabClick(0);
                }}
              >
                Cancel
              </Button>

              {activeTab === 1 ? (
                <Button
                  disabled={isUpdating}
                  type="button"
                  px={{ md: "15px", lg: "30px" }}
                  py="8px"
                  background="linear-gradient(134.59deg, #713EDD -1.98%, #A17DEE 111.17%)"
                  _hover={{
                    background:
                      "linear-gradient(134.59deg, #A17DEE -1.98%, #713EDD 111.17%)",
                  }}
                  color="white"
                  display="flex"
                  columnGap="5px"
                  onClick={handleSubmit(onSubmit)}
                >
                  {book ? "Update" : "Save"}
                  {isUpdating && <Spinner color="white" />}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleTabClick(1);
                  }}
                  px={{ md: "15px", lg: "30px" }}
                  py="8px"
                  background="linear-gradient(134.59deg, #713EDD -1.98%, #A17DEE 111.17%)"
                  _hover={{
                    background:
                      "linear-gradient(134.59deg, #A17DEE -1.98%, #713EDD 111.17%)",
                  }}
                  color="white"
                >
                  Next
                </Button>
              )}
            </Flex>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}
